/**
 * SkeletonGroup — supplies a default animation to every descendant Skeleton.
 *
 * A composite loader (card, table row, detail pane) is usually built from a
 * dozen placeholders that must all animate the same way. Rather than threading
 * `animation` through every level, wrap the loader once:
 *
 * @example
 * ```tsx
 * <SkeletonGroup animation="wave">
 *   <Skeleton variant="circular" width={40} />
 *   <SkeletonText lines={2} />
 * </SkeletonGroup>
 * ```
 *
 * The provider renders no view of its own, so it can be dropped anywhere in a
 * tree without affecting layout.
 */
import { createContext, useContext, type FC, type ReactNode } from "react";
import type { SkeletonAnimation } from "./Skeleton.types";

// `undefined` means "no group above me", which is distinct from a group that
// deliberately disables motion with `animation={false}`.
const SkeletonGroupContext = createContext<SkeletonAnimation | undefined>(undefined);

const DEFAULT_ANIMATION: SkeletonAnimation = "pulse";

export interface SkeletonGroupProps {
  /**
   * Animation every descendant Skeleton uses unless it sets its own.
   *
   * @default "pulse"
   */
  readonly animation?: SkeletonAnimation | undefined;
  readonly children?: ReactNode | undefined;
}

const SkeletonGroup: FC<SkeletonGroupProps> = ({ animation = DEFAULT_ANIMATION, children }) => (
  <SkeletonGroupContext.Provider value={animation}>{children}</SkeletonGroupContext.Provider>
);

SkeletonGroup.displayName = "SkeletonGroup";

/**
 * Resolves the animation for a single placeholder: an explicit prop wins, then
 * the enclosing group, then the `"pulse"` default.
 */
export function useSkeletonAnimation(animation?: SkeletonAnimation): SkeletonAnimation {
  const groupAnimation = useContext(SkeletonGroupContext);
  return animation ?? groupAnimation ?? DEFAULT_ANIMATION;
}

export { SkeletonGroup };
